import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './AdminReports.css';

function AdminReports() {
    const [loading, setLoading] = useState(false);
    const [statistics, setStatistics] = useState(null);
    const [dateRange, setDateRange] = useState({
        start_date: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0]
    });
    const [groupBy, setGroupBy] = useState('day');
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        fetchStatistics();
    }, []);

    const fetchStatistics = async () => {
        setLoading(true);
        try {
            const response = await api.get('/reports/print-statistics', {
                params: {
                    start_date: dateRange.start_date,
                    end_date: dateRange.end_date,
                    group_by: groupBy
                }
            });
            setStatistics(response.data);
            setMessage({ type: '', text: '' });
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.error || 'เกิดข้อผิดพลาดในการโหลดข้อมูล' });
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (field, value) => {
        setDateRange(prev => ({ ...prev, [field]: value }));
    };

    const handleSearch = () => {
        fetchStatistics();
    };

    const getDocumentTypeName = (type) => {
        const types = {
            'salary_slip': 'สลิปเงินเดือน',
            'certificate_50tawi': 'หนังสือรับรองภาษี (50 ทวิ)',
            'salary_certificate': 'หนังสือรับรองเงินเดือน',
            'yearly_summary': 'สรุปรายปี'
        };
        return types[type] || type;
    };

    const formatNumber = (num) => {
        return new Intl.NumberFormat('th-TH').format(num || 0);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString('th-TH', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatPeriod = (period) => {
        if (!period) return '';
        if (groupBy === 'month') {
            const [year, month] = period.split('-');
            const monthNames = ['', 'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
                'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
            return `${monthNames[parseInt(month)]} ${parseInt(year) + 543}`;
        } else {
            const date = new Date(period);
            return date.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' });
        }
    };

    // จัดกลุ่มข้อมูล time series ตามประเภทเอกสาร
    const groupTimeSeriesByType = () => {
        if (!statistics?.time_series) return {};

        const grouped = {};
        statistics.time_series.forEach(item => {
            if (!grouped[item.document_type]) {
                grouped[item.document_type] = [];
            }
            grouped[item.document_type].push(item);
        });
        return grouped;
    };

    const timeSeriesData = groupTimeSeriesByType();

    return (
        <div className="admin-reports">
            <div className="page-header">
                <h2>รายงานสถิติการใช้งานระบบ</h2>
                <p className="page-subtitle">ติดตามการใช้งานระบบอัตโนมัติสำหรับผู้บริหาร</p>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    {message.text}
                </div>
            )}

            {/* Filters */}
            <div className="steam-card mb-3">
                <div className="steam-card-header">
                    <h3>เลือกช่วงวันที่</h3>
                </div>
                <div className="steam-card-body">
                    <div className="filters-container">
                        <div className="filter-group">
                            <label>วันที่เริ่มต้น</label>
                            <input
                                type="date"
                                className="steam-input"
                                value={dateRange.start_date}
                                onChange={(e) => handleDateChange('start_date', e.target.value)}
                            />
                        </div>
                        <div className="filter-group">
                            <label>วันที่สิ้นสุด</label>
                            <input
                                type="date"
                                className="steam-input"
                                value={dateRange.end_date}
                                onChange={(e) => handleDateChange('end_date', e.target.value)}
                            />
                        </div>
                        <div className="filter-group">
                            <label>แสดงแบบ</label>
                            <select
                                className="steam-input"
                                value={groupBy}
                                onChange={(e) => setGroupBy(e.target.value)}
                            >
                                <option value="day">รายวัน</option>
                                <option value="month">รายเดือน</option>
                            </select>
                        </div>
                        <div className="filter-group">
                            <label>&nbsp;</label>
                            <button
                                className="btn-steam btn-primary"
                                onClick={handleSearch}
                                disabled={loading}
                            >
                                {loading ? 'กำลังโหลด...' : 'ค้นหา'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-container">
                    <div className="spinner"></div>
                    <p>กำลังโหลดข้อมูล...</p>
                </div>
            ) : statistics ? (
                <>
                    {/* Summary Cards */}
                    <div className="stats-grid mb-3">
                        <div className="stat-card primary">
                            <div className="stat-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M19 8H5c-1.66 0-3 1.34-3 3v6h4v4h12v-4h4v-6c0-1.66-1.34-3-3-3zm-3 11H8v-5h8v5zm3-7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm-1-9H6v4h12V3z"/>
                                </svg>
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{formatNumber(statistics.summary?.total_prints)}</div>
                                <div className="stat-label">ครั้งทั้งหมด</div>
                            </div>
                        </div>

                        <div className="stat-card success">
                            <div className="stat-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                    <circle cx="9" cy="7" r="4"/>
                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                </svg>
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{formatNumber(statistics.summary?.total_users)}</div>
                                <div className="stat-label">ผู้ใช้งาน (คน)</div>
                            </div>
                        </div>

                        <div className="stat-card info">
                            <div className="stat-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                                    <line x1="16" y1="2" x2="16" y2="6"/>
                                    <line x1="8" y1="2" x2="8" y2="6"/>
                                    <line x1="3" y1="10" x2="21" y2="10"/>
                                </svg>
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">{formatNumber(statistics.summary?.total_days)}</div>
                                <div className="stat-label">จำนวนวันที่มีการใช้งาน</div>
                            </div>
                        </div>

                        <div className="stat-card warning">
                            <div className="stat-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                                </svg>
                            </div>
                            <div className="stat-content">
                                <div className="stat-value">
                                    {statistics.summary?.total_days > 0
                                        ? Math.round(statistics.summary?.total_prints / statistics.summary?.total_days)
                                        : 0}
                                </div>
                                <div className="stat-label">เฉลี่ย (ครั้ง/วัน)</div>
                            </div>
                        </div>
                    </div>

                    {/* Document Type Statistics */}
                    <div className="steam-card mb-3">
                        <div className="steam-card-header">
                            <h3>สถิติตามประเภทเอกสาร</h3>
                        </div>
                        <div className="steam-card-body">
                            <div className="document-stats-grid">
                                {statistics.by_document_type?.map((item, index) => (
                                    <div key={index} className="document-stat-card">
                                        <div className="document-stat-header">
                                            <h4>{getDocumentTypeName(item.document_type)}</h4>
                                        </div>
                                        <div className="document-stat-body">
                                            <div className="stat-row">
                                                <span className="stat-label">จำนวนครั้ง:</span>
                                                <span className="stat-value">{formatNumber(item.total_count)} ครั้ง</span>
                                            </div>
                                            <div className="stat-row">
                                                <span className="stat-label">ผู้ใช้งาน:</span>
                                                <span className="stat-value">{formatNumber(item.unique_users)} คน</span>
                                            </div>
                                            <div className="stat-row">
                                                <span className="stat-label">เฉลี่ย:</span>
                                                <span className="stat-value">
                                                    {(item.total_count / item.unique_users).toFixed(1)} ครั้ง/คน
                                                </span>
                                            </div>
                                        </div>
                                        <div className="document-stat-progress">
                                            <div
                                                className="progress-bar"
                                                style={{
                                                    width: `${(item.total_count / statistics.summary?.total_prints * 100)}%`
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Time Series Chart */}
                    {Object.keys(timeSeriesData).length > 0 && (
                        <div className="steam-card mb-3">
                            <div className="steam-card-header">
                                <h3>แนวโน้มการใช้งานตามเวลา</h3>
                            </div>
                            <div className="steam-card-body">
                                <div className="time-series-table-wrapper">
                                    <table className="steam-table">
                                        <thead>
                                            <tr>
                                                <th>วันที่</th>
                                                {statistics.by_document_type?.map((item, index) => (
                                                    <th key={index}>{getDocumentTypeName(item.document_type)}</th>
                                                ))}
                                                <th>รวม</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                const periods = [...new Set(statistics.time_series.map(item => item.period))].sort();
                                                return periods.map((period, idx) => {
                                                    const periodData = statistics.time_series.filter(item => item.period === period);
                                                    const total = periodData.reduce((sum, item) => sum + item.count, 0);

                                                    return (
                                                        <tr key={idx}>
                                                            <td>{formatPeriod(period)}</td>
                                                            {statistics.by_document_type?.map((docType, index) => {
                                                                const data = periodData.find(item => item.document_type === docType.document_type);
                                                                return (
                                                                    <td key={index} className="text-center">
                                                                        {data ? formatNumber(data.count) : '-'}
                                                                    </td>
                                                                );
                                                            })}
                                                            <td className="text-center font-bold">{formatNumber(total)}</td>
                                                        </tr>
                                                    );
                                                });
                                            })()}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Top Users */}
                    {statistics.top_users && statistics.top_users.length > 0 && (
                        <div className="steam-card">
                            <div className="steam-card-header">
                                <h3>ผู้ใช้งานมากที่สุด (Top 10)</h3>
                            </div>
                            <div className="steam-card-body">
                                <div className="top-users-table-wrapper">
                                    <table className="steam-table">
                                        <thead>
                                            <tr>
                                                <th className="text-center">#</th>
                                                <th>รหัสบัตรประชาชน</th>
                                                <th>ชื่อ-นามสกุล</th>
                                                <th>หน่วยงาน</th>
                                                <th className="text-center">จำนวนครั้ง</th>
                                                <th className="text-center">ประเภทเอกสาร</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {statistics.top_users.map((user, index) => (
                                                <tr key={index}>
                                                    <td className="text-center">
                                                        <span className={`rank-badge rank-${index + 1}`}>
                                                            {index + 1}
                                                        </span>
                                                    </td>
                                                    <td>{user.cid}</td>
                                                    <td>{user.full_name || '-'}</td>
                                                    <td>{user.department || '-'}</td>
                                                    <td className="text-center font-bold">{formatNumber(user.print_count)}</td>
                                                    <td className="text-center">{formatNumber(user.document_types_count)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <div className="empty-state">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
                    </svg>
                    <p>เลือกช่วงวันที่และคลิก "ค้นหา" เพื่อดูรายงาน</p>
                </div>
            )}
        </div>
    );
}

export default AdminReports;
